import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";

const WIRIS_CREATE_IMAGE_URL =
  "https://www.wiris.net/demo/plugins/app/createimage";
const WIRIS_RENDER_URL = "https://www.wiris.net/demo/editor/render";
const mathImageCache = new Map<string, string>();
const MAX_WIRIS_RENDER_URL_LENGTH = 12000;
const WIRIS_REQUEST_TIMEOUT_MS = 8000;
const MAX_NORMALIZATION_PASSES = 12;
const MAX_URI_DECODE_PASSES = 4;
const ENCODED_MATH_TAG_PATTERN =
  /(?:&lt;|&#60;|&#x3c;|&amp;lt;|\\u003c|«|§lt;)\s*math\b/i;

function decodeHtmlEntities(value: string): string {
  if (typeof document === "undefined") {
    return value;
  }
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
}

function decodeUnicodeMathEscapes(value: string): string {
  return value
    .replace(/\\u003[cC]/g, "<")
    .replace(/\\u003[eE]/g, ">")
    .replace(/\\u0026/g, "&")
    .replace(/\\u0022/g, '"')
    .replace(/\\\//g, "/");
}

function decodeWirisSafeChars(value: string): string {
  return value
    .replace(/§/g, "&")
    .replace(/¨/g, '"')
    .replace(/`/g, "'");
}

function normalizeAngleBrackets(value: string): string {
  return value.replace(/«/g, "<").replace(/»/g, ">");
}

function unwrapJsonString(value: string): string {
  let normalized = value.trim();
  for (let i = 0; i < 10; i += 1) {
    if (!normalized) break;
    if (
      !(
        (normalized.startsWith('"') && normalized.endsWith('"')) ||
        (normalized.startsWith("'") && normalized.endsWith("'"))
      )
    ) {
      break;
    }
    try {
      const parsed = JSON.parse(normalized);
      if (typeof parsed !== "string") {
        break;
      }
      normalized = parsed;
    } catch {
      break;
    }
  }
  return normalized;
}

function decodeMarkupPass(value: string): string {
  return normalizeAngleBrackets(
    decodeUnicodeMathEscapes(
      decodeWirisSafeChars(decodeHtmlEntities(unwrapJsonString(value)))
    )
  );
}

function decodeMarkupRepeatedly(value: string): string {
  let normalized = value;
  for (let i = 0; i < MAX_NORMALIZATION_PASSES; i += 1) {
    const next = decodeMarkupPass(normalized);
    if (next === normalized) {
      break;
    }
    normalized = next;
  }
  return normalized;
}

function decodeUriComponentRepeatedly(value: string): string {
  let normalized = value;
  for (let i = 0; i < MAX_URI_DECODE_PASSES; i += 1) {
    try {
      const next = decodeURIComponent(normalized.replace(/\+/g, "%20"));
      if (next === normalized) {
        break;
      }
      normalized = next;
    } catch {
      break;
    }
  }
  return normalized;
}

function extractMathFromWirisHtml(value: string): string | null {
  const html = decodeMarkupRepeatedly(value);
  const dataMathmlMatch = html.match(/data-mathml\s*=\s*(["'])([\s\S]*?)\1/i);
  if (dataMathmlMatch?.[2]) {
    const extracted = decodeMarkupRepeatedly(dataMathmlMatch[2]);
    if (
      /<math(?=[\s>])/i.test(extracted) ||
      ENCODED_MATH_TAG_PATTERN.test(extracted)
    ) {
      return extracted;
    }
  }

  const srcMatch = html.match(/src\s*=\s*(["'])([\s\S]*?)\1/i);
  if (srcMatch?.[2]) {
    const decodedSrc = decodeUriComponentRepeatedly(srcMatch[2]);
    const normalizedSrc = decodeMarkupRepeatedly(decodedSrc);
    const mathMatch = normalizedSrc.match(/MathML:\s*(<math[\s\S]*?<\/math>)/i);
    if (mathMatch?.[1]) {
      return decodeMarkupRepeatedly(mathMatch[1]);
    }
    const mmlParamMatch = normalizedSrc.match(/[?&]mml=([^&]+)/i);
    if (mmlParamMatch?.[1]) {
      const decodedMml = decodeMarkupRepeatedly(
        decodeUriComponentRepeatedly(mmlParamMatch[1])
      );
      if (
        /<math(?=[\s>])/i.test(decodedMml) ||
        ENCODED_MATH_TAG_PATTERN.test(decodedMml)
      ) {
        return decodedMml;
      }
    }
  }

  return null;
}

function normalizeMathMarkup(value: string): string {
  if (!value) {
    return value;
  }
  let normalized = decodeMarkupRepeatedly(value);

  if (!/<math(?=[\s>])/i.test(normalized)) {
    const wirisMath = extractMathFromWirisHtml(normalized);
    if (wirisMath) {
      normalized = decodeMarkupRepeatedly(wirisMath);
    }
  }

  if (
    !/<math(?=[\s>])/i.test(normalized) &&
    !ENCODED_MATH_TAG_PATTERN.test(normalized)
  ) {
    return normalized;
  }

  return decodeMarkupRepeatedly(normalized);
}

function containsMathTag(value: string): boolean {
  return /<math(?=[\s>])/i.test(value);
}

function ensureMathMLNamespace(mathml: string): string {
  if (mathml.includes("xmlns")) return mathml;
  return mathml.replace(
    /^<math(?=[\s>])/,
    '<math xmlns="http://www.w3.org/1998/Math/MathML"'
  );
}

function stripMathAnnotations(mathml: string): string {
  if (typeof document === "undefined") {
    return mathml;
  }
  try {
    const container = document.createElement("div");
    container.innerHTML = ensureMathMLNamespace(mathml);
    const math = container.querySelector("math");
    if (!math) {
      return mathml;
    }
    math.querySelectorAll("annotation, annotation-xml").forEach((node) => {
      node.remove();
    });
    return math.outerHTML;
  } catch {
    return mathml;
  }
}

function getMathRenderCandidates(mathml: string): string[] {
  const withNamespace = ensureMathMLNamespace(mathml);
  const stripped = stripMathAnnotations(withNamespace);
  return stripped !== withNamespace
    ? [withNamespace, stripped]
    : [withNamespace];
}

function getWirisRenderUrl(mathml: string): string {
  const clean = ensureMathMLNamespace(mathml);
  return `${WIRIS_RENDER_URL}?mml=${encodeURIComponent(clean)}&format=svg`;
}

function parseWirisImageResponse(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed.startsWith("data:image/")) {
    return trimmed;
  }
  if (trimmed.length > 100) {
    return `data:image/png;base64,${trimmed}`;
  }
  return null;
}

async function requestWirisMathImage(
  mathml: string,
  metrics: boolean
): Promise<string | null> {
  const body = new URLSearchParams({
    mml: mathml,
    base64: "true",
  });
  if (metrics) {
    body.set("metrics", "true");
  }

  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = setTimeout(() => {
    controller?.abort();
  }, WIRIS_REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(WIRIS_CREATE_IMAGE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body,
      signal: controller?.signal,
    });

    if (!response.ok) {
      return null;
    }

    return parseWirisImageResponse(await response.text());
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function fetchWirisMathImage(mathml: string): Promise<string | null> {
  const candidates = getMathRenderCandidates(mathml);
  for (const candidate of candidates) {
    try {
      const withMetrics = await requestWirisMathImage(candidate, true);
      if (withMetrics) {
        return withMetrics;
      }
      const withoutMetrics = await requestWirisMathImage(candidate, false);
      if (withoutMetrics) {
        return withoutMetrics;
      }
    } catch {
      // Try next candidate
    }
  }
  return null;
}

function createWirisImage(
  mathml: string,
  ownerDocument: Document
): HTMLImageElement | null {
  const wiris = (window as any).WirisPlugin;
  const parser = wiris?.Parser;
  if (!parser?.mathmlToImgObject) {
    return null;
  }
  const candidates = getMathRenderCandidates(mathml);
  for (const candidate of candidates) {
    try {
      const img = parser.mathmlToImgObject(ownerDocument, candidate, null, null);
      if (!img || !img.src) {
        continue;
      }
      img.style.display = "inline-block";
      img.style.verticalAlign = "middle";
      img.style.maxWidth = "100%";
      img.crossOrigin = "anonymous";
      return img;
    } catch {
      // Try next candidate
    }
  }
  return null;
}

function createMathImage(dataUrl: string): HTMLImageElement {
  const img = document.createElement("img");
  img.src = dataUrl;
  img.alt = "Math formula";
  img.style.display = "inline-block";
  img.style.verticalAlign = "middle";
  img.style.maxWidth = "100%";
  img.crossOrigin = "anonymous";
  return img;
}

async function replaceMathMLWithImages(
  container: HTMLElement
): Promise<{ mathCount: number; replacedCount: number }> {
  const mathElements = Array.from(container.querySelectorAll("math"));
  if (mathElements.length === 0) {
    return { mathCount: 0, replacedCount: 0 };
  }
  let replacedCount = 0;

  await Promise.all(
    mathElements.map(async (mathEl) => {
      const rawMathml = mathEl.outerHTML;
      const canonicalMathml = ensureMathMLNamespace(rawMathml);
      const normalizedMathml = stripMathAnnotations(canonicalMathml);
      const cached =
        mathImageCache.get(canonicalMathml) ??
        mathImageCache.get(normalizedMathml) ??
        mathImageCache.get(rawMathml);
      if (cached) {
        mathEl.replaceWith(createMathImage(cached));
        replacedCount += 1;
        return;
      }
      // Deterministic path: always prefer WIRIS render URL as <img src>.
      // This avoids browser MathML differences and plugin timing issues.
      const renderUrl = getWirisRenderUrl(canonicalMathml);
      if (renderUrl.length <= MAX_WIRIS_RENDER_URL_LENGTH) {
        mathImageCache.set(canonicalMathml, renderUrl);
        mathImageCache.set(normalizedMathml, renderUrl);
        mathImageCache.set(rawMathml, renderUrl);
        mathEl.replaceWith(createMathImage(renderUrl));
        replacedCount += 1;
        return;
      }
      const wirisImg = createWirisImage(canonicalMathml, container.ownerDocument);
      if (wirisImg) {
        mathImageCache.set(canonicalMathml, wirisImg.src);
        mathImageCache.set(normalizedMathml, wirisImg.src);
        mathImageCache.set(rawMathml, wirisImg.src);
        mathEl.replaceWith(wirisImg);
        replacedCount += 1;
        return;
      }
      const fetched = await fetchWirisMathImage(canonicalMathml);
      if (fetched) {
        mathImageCache.set(canonicalMathml, fetched);
        mathImageCache.set(normalizedMathml, fetched);
        mathImageCache.set(rawMathml, fetched);
        mathEl.replaceWith(createMathImage(fetched));
        replacedCount += 1;
        return;
      }
      // Final fallback for extremely long/invalid payloads: keep original MathML.
      // It may still render natively on supporting browsers.
    })
  );
  return { mathCount: mathElements.length, replacedCount };
}

async function waitForImages(
  container: HTMLElement,
  timeout: number = 3000
): Promise<void> {
  const images = Array.from(container.querySelectorAll("img"));
  if (images.length === 0) return;
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalHeight !== 0) {
            resolve();
            return;
          }
          const timeoutId = window.setTimeout(resolve, timeout);
          img.onload = () => {
            window.clearTimeout(timeoutId);
            resolve();
          };
          img.onerror = () => {
            window.clearTimeout(timeoutId);
            resolve();
          };
        })
    )
  );
}

const SANITIZE_OPTIONS: DOMPurify.Config = {
    USE_PROFILES: { mathMl: true, html: true },
    ADD_TAGS: [
      "math",
      "mrow",
      "mi",
      "mo",
      "mn",
      "msup",
      "msub",
      "msubsup",
      "mfrac",
      "msqrt",
      "mroot",
      "mover",
      "munder",
      "munderover",
      "mfenced",
      "mstyle",
      "mtext",
      "mtable",
      "mtr",
      "mtd",
      "menclose",
      "semantics",
      "annotation",
      "annotation-xml",
    ],
    ADD_ATTR: [
      "style",
      "class",
      "mathsize",
      "mathcolor",
      "mathbackground",
      "displaystyle",
      "mathvariant",
      "rowspan",
      "columnspan",
      "columnalign",
      "rowalign",
      "linethickness",
      "accent",
      "accentunder",
      "stretchy",
      "fence",
      "separator",
      "separators",
      "open",
      "close",
      "form",
      "minsize",
      "maxsize",
      "lspace",
      "rspace",
      "symmetric",
      "linebreak",
      "linebreakstyle",
      "scriptlevel",
      "xmlns",
      "xmlns:xlink",
      "xlink:href",
      "width",
      "height",
      "alt",
      "src",
      "encoding",
    ],
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|mailto|tel|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-]|$))/i,
  };

export const sanitizeMathML = (value: string): string =>
  DOMPurify.sanitize(value, SANITIZE_OPTIONS);

interface MathMLContentProps {
  html: string;
  style?: React.CSSProperties;
  className?: string;
  onError?: (error: unknown) => void;
}

export const MathMLContent: React.FC<MathMLContentProps> = ({
  html,
  style,
  className,
  onError,
}) => {
  const [renderHtml, setRenderHtml] = useState("");
  const [textFallback, setTextFallback] = useState<string | null>(null);
  const normalizedHtml = useMemo(() => normalizeMathMarkup(html), [html]);
  const fallbackHtml = useMemo(
    () => sanitizeMathML(normalizedHtml),
    [normalizedHtml]
  );

  useEffect(() => {
    let cancelled = false;
    setTextFallback(null);
    setRenderHtml(fallbackHtml);

    const process = async () => {
      try {
        if (!containsMathTag(normalizedHtml)) {
          return;
        }

        const tempContainer = document.createElement("div");
        tempContainer.innerHTML = normalizedHtml;
        await replaceMathMLWithImages(tempContainer);
        await waitForImages(tempContainer);
        const nextHtml = sanitizeMathML(tempContainer.innerHTML);
        if (!cancelled) {
          setTextFallback(null);
          setRenderHtml(nextHtml);
        }
      } catch (error) {
        onError?.(error);
      }
    };

    process();

    return () => {
      cancelled = true;
    };
  }, [fallbackHtml, normalizedHtml, onError]);

  return (
    <div
      className={className}
      style={style}
      {...(textFallback === null
        ? { dangerouslySetInnerHTML: { __html: renderHtml || fallbackHtml } }
        : {})}
    >
      {textFallback !== null ? textFallback : null}
    </div>
  );
};
